import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { apiRequest } from '../api';

const UserModal = ({ user, onClose, onSave, universityId, token, apiUrl }) => {
  const [firstName, setFirstName] = useState(user ? user.first_name || user.name?.split(' ')[0] || '' : '');
  const [lastName, setLastName] = useState(user ? user.last_name || user.name?.split(' ')[1] || '' : '');
  const [email, setEmail] = useState(user ? user.email : '');
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFeedback('');
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      role: 'user',
      university_id: universityId,
    };
    const endpoint = user ? `${apiUrl}/users/${user.id}` : `${apiUrl}/users`;
    const method = user ? 'PUT' : 'POST';
    try {
      await apiRequest(endpoint, method, payload, token);
      setFeedback(user ? 'User updated successfully.' : 'User created successfully. Activation email sent.');
      onSave();
      setTimeout(onClose, 1200);
    } catch (error) {
      setFeedback(error.message || 'An error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div style={{ backgroundColor: '#fff', padding: 24, borderRadius: 8, width: 400 }}>
        <h4>{user ? 'Edit User' : 'Add User'}</h4>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name</label>
            <input type="text" className="form-control" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input type="text" className="form-control" value={lastName} onChange={e => setLastName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" className="form-control" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
          </div>
          {feedback && <div className="alert alert-info" style={{ marginTop: 12 }}>{feedback}</div>}
        </form>
      </div>
    </div>
  );
};

const UniAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Filter and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const universityId = localStorage.getItem('universityId');
  const token = localStorage.getItem('token');
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        // Build query parameters for filtering and sorting
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (activeFilter !== '') params.append('is_active', activeFilter);
        if (sortBy) params.append('sort_by', sortBy);
        if (sortOrder) params.append('sort_order', sortOrder);
        
        const data = await apiRequest(`${apiUrl}/users/university/${universityId}?${params.toString()}`, 'GET', null, token);
        setUsers(data);
      } catch (err) {
        setError('Failed to fetch users.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [apiUrl, universityId, token, searchTerm, activeFilter, sortBy, sortOrder]);

  const handleStatusToggle = async (userId, isActive) => {
    setError("");
    setFeedback("");
    try {
      const url = `${apiUrl}/users/${userId}/${isActive ? 'disable' : 'activate'}`;
      await apiRequest(url, 'PUT', null, token);
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === userId
          ? { ...user, is_active: !isActive, status: !isActive ? 'Active' : 'Inactive' }
          : user
      ));
      setFeedback('User status updated.');
      setTimeout(() => setFeedback(""), 2000);
    } catch (err) {
      setError('Failed to update user status.');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleModalSave = () => {
    // Refresh users after add/edit
    setShowModal(false);
    setEditingUser(null);
    setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiRequest(`${apiUrl}/users/university/${universityId}`, 'GET', null, token);
        setUsers(data);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  return (
    <div className="dashboard">
      <Sidebar />
      <main className="main-content">
        <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2>University Users</h2>
          <button className="btn btn-primary" onClick={handleAddUser}>Add User</button>
        </div>
        <hr />
        {feedback && <div className="alert alert-success" style={{ marginBottom: 16 }}>{feedback}</div>}
        {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}
        
        {/* Filter and Sort Controls */}
        <div className="mb-3">
          <div className="row">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-control"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="first_name">Name</option>
                <option value="email">Email</option>
                <option value="created_on">Created Date</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
            <div className="col-md-1">
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('');
                  setSortBy('first_name');
                  setSortOrder('asc');
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <p>Loading users...</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.name || `${user.first_name || ''} ${user.last_name || ''}`}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={user.is_active ? 'text-success' : 'text-danger'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{user.role || (user.roles && user.roles[0]?.name) || 'User'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      style={{ marginRight: 8 }}
                      onClick={() => handleEditUser(user)}
                    >
                      Edit
                    </button>
                    {user.is_active ? (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleStatusToggle(user.id, true)}
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleStatusToggle(user.id, false)}
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {showModal && (
          <UserModal
            user={editingUser}
            onClose={() => setShowModal(false)}
            onSave={handleModalSave}
            universityId={universityId}
            token={token}
            apiUrl={apiUrl}
          />
        )}
      </main>
    </div>
  );
};

export default UniAdminUsers; 